import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Ban, Download, FileCheck2, Mail, PenLine, Plus, RefreshCw, Send, Settings2, Trash2, UserPlus, X } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Botao, Segmentado, Selo } from '../../componentes/base';
import { Carregando, Erro, Vazio } from '../../componentes/estados';
import { aceitoDaUrl, uuidDaUrl } from '../../componentes/filtros-lista';
import { AreaSensivel, BuscaRemota, EntradaSensivel, Formulario, Rotulo, Selecao, type DefCampo } from '../../componentes/formulario';
import { Confirmar, Drawer, Modal } from '../../componentes/sobreposicao';
import { Tabela, type Coluna } from '../../componentes/tabela';
import { useToast } from '../../componentes/toast';
import { api, del, ErroRequisicao, get, mensagemDeErro, post } from '../../nucleo/api';
import { data, dataHora, email, nomePessoa, textoLivre } from '../../nucleo/formato';
import { escolhaDeEmpresaAntecedencia } from '../../nucleo/notificacoes/AntecedenciaAvisos';
import { podeNoWorkspace, useSessao } from '../../nucleo/sessao';
import { HashCurto, Linha } from './comum';
import { COR_ASSINATURA, COR_SIGNATARIO, ESTADOS_ASSINATURA, invalidarDocumentos, PROVEDORES, type AcessoCliente, type Assinatura, type AssinaturaDetalhe, type EstadoAssinatura, type PapelSignatario, type ProvedorAssinatura, type ProvedorPadrao, type SugestaoSignatarios } from './tipos';

type Filtro = 'todas' | 'minhas';

/** Assinaturas eletrônicas: os envios, com o filtro "esperando a minha assinatura". Abre pela URL com `?id=`. */
export function Assinaturas() {
  const { t } = useTranslation('documentos');
  const s = useSessao();
  const [busca, setBusca] = useSearchParams();
  const [estado, setEstado] = useState<EstadoAssinatura | ''>((aceitoDaUrl(busca.get('estado'), ESTADOS_ASSINATURA) as EstadoAssinatura | ''));
  const [filtro, setFiltro] = useState<Filtro>(busca.get('minhas') === 'true' ? 'minhas' : 'todas');
  const [nova, setNova] = useState(false);
  const [padrao, setPadrao] = useState(false);
  const [aberta, setAberta] = useState<string | null>(uuidDaUrl(busca.get('id')) || null);
  useEffect(() => {
    const id = uuidDaUrl(busca.get('id'));
    if (id) setAberta(id);
  }, [busca]);
  const fechar = () => {
    setAberta(null);
    if (busca.get('id')) setBusca((b: URLSearchParams) => (b.delete('id'), b), { replace: true });
  };
  const colunas: Coluna<Assinatura>[] = [
    { chave: 'documento_nome', rotulo: t('campos.documento'), principal: true, render: (a) => <span className="font-medium">{textoLivre(a.documento_nome)}</span> },
    { chave: 'signatarios', rotulo: t('campos.signatarios'), render: (a) => a.signatarios.map((x) => nomePessoa(x.nome)).join(', ') || '-' },
    { chave: 'versao', rotulo: t('campos.versao'), mono: true, oculta: true, render: (a) => t('versaoN', { versao: a.versao }) },
    { chave: 'prazo', rotulo: t('campos.prazo'), render: (a) => (a.prazo ? data(a.prazo) : '-') },
    { chave: 'provedor', rotulo: t('campos.provedor'), oculta: true, render: (a) => t(`provedor.${a.provedor}`) },
    { chave: 'enviada_em', rotulo: t('campos.enviada'), render: (a) => (a.enviada_em ? data(a.enviada_em) : '-') },
    { chave: 'estado', rotulo: t('campos.estado'), render: (a) => <Selo cor={COR_ASSINATURA[a.estado]}>{t(`estado.${a.estado}`)}</Selo> },
  ];
  const params = useMemo(() => ({ ...(estado ? { estado } : {}), ...(filtro === 'minhas' ? { minhas: 'true' } : {}) }), [estado, filtro]);
  return (
    <>
      {s.pode('documentos.criar') && (
        <AcaoPrimaria>
          <Botao variante="primario" icone={<Plus className="size-4" />} onClick={() => setNova(true)}>
            {t('acoes.enviarAssinatura')}
          </Botao>
        </AcaoPrimaria>
      )}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <Segmentado<Filtro> rotulo={t('campos.signatarios')} valor={filtro} aoMudar={setFiltro} opcoes={(['todas', 'minhas'] as const).map((v) => ({ valor: v, rotulo: t(`filtro.${v}`) }))} />
        {s.pode('documentos.configurar') && (
          <Botao variante="fantasma" tamanho="sm" icone={<Settings2 className="size-4" />} onClick={() => setPadrao(true)}>
            {t('acoes.provedorPadrao')}
          </Botao>
        )}
      </div>
      <Tabela<Assinatura>
        tela="documentos.assinaturas"
        caminho="/documentos/assinaturas"
        params={params}
        chaveConsulta={['documentos', 'assinaturas', estado, filtro]}
        colunas={colunas}
        aoClicar={(a) => setAberta(a.id)}
        buscaNaUrl
        filtros={{ estado }}
        aoAplicarFiltros={(f) => setEstado(aceitoDaUrl(String(f.estado ?? ''), ESTADOS_ASSINATURA) as EstadoAssinatura | '')}
        filtrosExtra={
          <Selecao
            aria-label={t('campos.estado')}
            value={estado}
            onChange={(e) => setEstado(e.target.value as EstadoAssinatura | '')}
            opcoes={[{ valor: '', rotulo: t('estado.todos') }, ...ESTADOS_ASSINATURA.map((x) => ({ valor: x, rotulo: t(`estado.${x}`) }))]}
          />
        }
        vazio={<Vazio texto={filtro === 'minhas' ? t('vazio.minhas') : t('vazio.assinaturas')} />}
      />
      {nova && <FormAssinatura aoFechar={() => setNova(false)} />}
      {aberta && <FichaAssinatura id={aberta} aoFechar={fechar} />}
      {padrao && <ProvedorPadraoModal aoFechar={() => setPadrao(false)} />}
    </>
  );
}

/** Rótulo do provedor na escolha: o desconectado aparece, mas avisa (o servidor recusa o envio por ele). */
const rotuloProvedor = (t: (k: string, o?: Record<string, unknown>) => string, p: ProvedorAssinatura, conectados?: Record<ProvedorAssinatura, boolean>) =>
  conectados && !conectados[p] ? t('provedor.naoConectado', { nome: t(`provedor.${p}`) }) : t(`provedor.${p}`);

/**
 * A1 (20/10): provedor padrão da assinatura, do workspace ou próprio de cada empresa (como a antecedência dos avisos, B7).
 * Quem envia pode trocar em cada envio.
 */
function ProvedorPadraoModal({ aoFechar }: { aoFechar: () => void }) {
  const { t } = useTranslation('documentos');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const escolha = escolhaDeEmpresaAntecedencia(s);
  const [empresa, setEmpresa] = useState(escolha.inicial);
  const [provedor, setProvedor] = useState<ProvedorAssinatura | ''>('');
  const params = empresa ? { empresa_id: empresa } : {};
  const q = useQuery({ queryKey: ['documentos', 'provedor-padrao', empresa], queryFn: () => get<ProvedorPadrao>('/documentos/assinaturas/provedor-padrao', params) });
  const valor = provedor || q.data?.provedor || '';
  // D24 L2: quem assina pela empresa (configuração do grupo: só quem vê todas as empresas muda).
  // Com uma empresa só, o seletor não aparece (fica o padrão do workspace): o assinante é o dessa empresa.
  const empresaDoAssinante = empresa || (s.empresas?.opcoes.length === 1 ? s.empresas.opcoes[0]!.id : '');
  const podeAssinante = !!empresaDoAssinante && podeNoWorkspace(s, 'documentos.configurar');
  const assinante = useQuery({
    queryKey: ['documentos', 'assinante', empresaDoAssinante],
    queryFn: () => get<{ assinante: { usuario_id: string; nome: string } | null }>('/documentos/assinaturas/assinante', { empresa_id: empresaDoAssinante }),
    enabled: podeAssinante,
  });
  const [novoAssinante, setNovoAssinante] = useState<string | null | undefined>(undefined);
  const salvar = useMutation({
    mutationFn: async (corpo: Record<string, unknown>) => {
      const mudouAssinante = podeAssinante && novoAssinante !== undefined && novoAssinante !== (assinante.data?.assinante?.usuario_id ?? null);
      if (mudouAssinante) await api('PUT', '/documentos/assinaturas/assinante', { empresa_id: empresaDoAssinante, usuario_id: novoAssinante });
      // Só o assinante mudou: o provedor da empresa fica como estava (salvar o mesmo provedor o tornaria próprio da empresa).
      if (corpo.herdar || provedor || !mudouAssinante) await api('PUT', '/documentos/assinaturas/provedor-padrao', { ...corpo, ...params });
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['documentos', 'assinante'] });
      await qc.invalidateQueries({ queryKey: ['documentos', 'provedor-padrao'] });
      toast.sucesso(tn('comum.salvo'));
      aoFechar();
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      titulo={t('acoes.provedorPadrao')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" carregando={salvar.isPending} aria-disabled={!valor} onClick={() => valor && salvar.mutate({ provedor: valor })}>
            {t('acoes.salvar')}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-sm text-tinta-3">{t('ajuda.provedorPadrao')}</p>
        {escolha.mostra && (
          <Rotulo id="provedor-empresa" rotulo={t('campos.empresa')}>
            <Selecao
              id="provedor-empresa"
              value={empresa}
              onChange={(e) => (setEmpresa(e.target.value), setProvedor(''), setNovoAssinante(undefined))}
              opcoes={[...(escolha.comPadrao ? [{ valor: '', rotulo: t('ajuda.padraoWorkspace') }] : []), ...(s.empresas?.opcoes ?? []).map((x) => ({ valor: x.id, rotulo: x.nome }))]}
            />
          </Rotulo>
        )}
        {q.isPending ? (
          <Carregando />
        ) : q.isError ? (
          <Erro erro={q.error} aoTentar={() => q.refetch()} />
        ) : (
          <>
            <Rotulo id="provedor-padrao" rotulo={t('campos.provedor')}>
              <Selecao id="provedor-padrao" value={valor} onChange={(e) => setProvedor(e.target.value as ProvedorAssinatura)} opcoes={PROVEDORES.map((p) => ({ valor: p, rotulo: rotuloProvedor(t, p, q.data.conectados) }))} />
            </Rotulo>
            {empresa && (
              <div className="flex flex-wrap items-center gap-2 text-sm text-tinta-3">
                {q.data.proprio ? <Selo>{t('ajuda.proprio')}</Selo> : <span>{t('ajuda.herdado')}</span>}
                {q.data.proprio && (
                  <button type="button" onClick={() => salvar.mutate({ herdar: true })} disabled={salvar.isPending} className="text-xs text-acento-forte underline">
                    {t('acoes.voltarPadrao')}
                  </button>
                )}
              </div>
            )}
            {podeAssinante &&
              (assinante.isPending ? (
                <Carregando />
              ) : assinante.isError ? (
                <Erro erro={assinante.error} aoTentar={() => assinante.refetch()} />
              ) : (
                <Rotulo id="assinante-empresa" rotulo={t('campos.assinante')} ajuda={t('ajuda.assinante')}>
                  <BuscaRemota
                    key={empresaDoAssinante}
                    id="assinante-empresa"
                    def={{ caminho: '/membros', rotulo: (i) => String(i.nome ?? ''), pessoa: true }}
                    valor={novoAssinante === undefined ? (assinante.data.assinante?.usuario_id ?? null) : novoAssinante}
                    rotuloAtual={assinante.data.assinante?.nome}
                    aoMudar={(v) => setNovoAssinante(v)}
                  />
                </Rotulo>
              ))}
          </>
        )}
      </div>
    </Modal>
  );
}

interface NovoSignatario {
  chave: string;
  tipo: 'interno' | 'cliente' | 'externo';
  /** D24 L2: o papel no contrato (sugestão do contrato gerado de modelo). */
  papel?: PapelSignatario;
  usuario_id?: string;
  parceiro_id?: string;
  acesso_id?: string;
  nome: string;
  email?: string;
}

/**
 * Prepara o envio: quem assina (pessoas da equipe e pessoas do cliente com acesso ao Portal), o recado e o prazo. "Salvar e
 * enviar" fixa a versão e o hash do arquivo e avisa quem assina.
 */
export function FormAssinatura({ documento, aoFechar }: { documento?: { id: string; nome: string; tipo?: string; registro_tipo: string | null; registro_id: string | null }; aoFechar: () => void }) {
  const { t } = useTranslation('documentos');
  const { t: tn } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const id = useMemo(() => crypto.randomUUID(), []);
  const [lista, setLista] = useState<NovoSignatario[]>([]);
  const [parceiro, setParceiro] = useState<string | null>(documento?.registro_tipo === 'cadastros.parceiro' ? documento.registro_id : null);
  const [acesso, setAcesso] = useState('');
  const sessao = useSessao();
  // Val5 (29/10): sem `documentos.enviar`, o formulário só salva o rascunho.
  const enviarDepois = useRef(sessao.pode('documentos.enviar'));
  const empresaAtual = sessao.empresas?.atual;
  // Rodada rval1-1 (RVAL1-ROB-M2): sem empresa atual (troca de empresa desligada ou "Minhas verticais"), o padrão sugerido é o
  // da empresa do documento (o mesmo que o servidor usaria no envio), não o do workspace.
  const paramsPadrao = empresaAtual ? { empresa_id: empresaAtual } : documento?.id ? { documento_id: documento.id } : {};
  const padrao = useQuery({
    queryKey: ['documentos', 'provedor-padrao', empresaAtual ?? '', documento?.id ?? ''],
    queryFn: () => get<ProvedorPadrao>('/documentos/assinaturas/provedor-padrao', paramsPadrao),
  });
  const [escolhido, setEscolhido] = useState<ProvedorAssinatura | null>(null);
  const provedor = escolhido ?? padrao.data?.provedor ?? 'nativa';
  const externo = provedor !== 'nativa';
  const [novoNome, setNovoNome] = useState('');
  const [novoEmail, setNovoEmail] = useState('');
  // D24 L2: o contrato gerado de modelo .docx já traz quem assina (cliente, garantidor e quem assina pela empresa) e o
  // provedor que o documento cita; a pessoa confere e troca. Sem PDF, a resposta (422) explica e o envio fica barrado.
  const doContrato = documento?.tipo === 'contrato' && documento.registro_tipo === 'assinaturas.contrato';
  const sugestao = useQuery({
    queryKey: ['documentos', 'signatarios-contrato', documento?.id],
    queryFn: () => get<SugestaoSignatarios>('/documentos/contratos/signatarios', { documento_id: documento!.id }),
    enabled: doContrato,
    retry: false,
  });
  const aplicada = useRef(false);
  useEffect(() => {
    const d = sugestao.data;
    if (!d || aplicada.current) return;
    aplicada.current = true;
    setEscolhido(d.provedor);
    setLista(
      d.signatarios.map((x) => ({
        chave: crypto.randomUUID(),
        tipo: x.tipo,
        papel: x.papel,
        nome: x.nome,
        email: x.email ?? undefined,
        ...(x.tipo === 'interno' ? { usuario_id: x.usuario_id } : { parceiro_id: x.parceiro_id ?? undefined }),
        ...(x.tipo === 'cliente' ? { acesso_id: x.acesso_id } : {}),
      })),
    );
  }, [sugestao.data]);
  const semPdf = doContrato && sugestao.error instanceof ErroRequisicao && sugestao.error.corpo.code === 'documento.contrato_sem_pdf';
  const acessos = useQuery({
    queryKey: ['documentos', 'signatarios-cliente', parceiro],
    queryFn: () => get<{ itens: AcessoCliente[]; portal: boolean }>('/documentos/signatarios-cliente', { parceiro_id: parceiro }),
    enabled: !!parceiro,
  });
  const mesma = (x: NovoSignatario, n: Omit<NovoSignatario, 'chave'>) =>
    n.tipo === 'interno' ? x.usuario_id === n.usuario_id : n.tipo === 'cliente' ? x.acesso_id === n.acesso_id : x.tipo === 'externo' && x.email === n.email;
  const incluir = (n: Omit<NovoSignatario, 'chave'>) => setLista((l) => (l.some((x) => mesma(x, n)) ? l : [...l, { ...n, chave: crypto.randomUUID() }]));
  const incluirEmail = () => {
    const nome = novoNome.trim();
    const mail = novoEmail.trim().toLowerCase();
    if (!nome || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(mail)) return;
    incluir({ tipo: 'externo', nome, email: mail, ...(parceiro ? { parceiro_id: parceiro } : {}) });
    setNovoNome('');
    setNovoEmail('');
  };
  // Na nativa, quem assina é da equipe ou do cliente pelo Portal: quem entrou só por e-mail sai da lista.
  const trocarProvedor = (p: ProvedorAssinatura) => {
    setEscolhido(p);
    if (p === 'nativa') setLista((l) => l.filter((x) => x.tipo !== 'externo'));
  };
  const campos: DefCampo[] = [
    ...(documento ? [] : [{ nome: 'documento_id', rotulo: t('campos.documento'), tipo: 'busca', obrigatorio: true, busca: { caminho: '/documentos/documentos', rotulo: (i: Record<string, unknown>) => String(i.nome ?? '') } } as DefCampo]),
    { nome: 'mensagem', rotulo: t('campos.mensagem'), tipo: 'area' },
    { nome: 'prazo', rotulo: t('campos.prazo'), tipo: 'data', largura: 'meia', ajuda: t('campos.prazoAjuda') },
  ];
  return (
    <Drawer
      aberto
      aoFechar={aoFechar}
      largura="lg"
      titulo={documento ? `${t('acoes.enviarAssinatura')}: ${textoLivre(documento.nome)}` : t('acoes.enviarAssinatura')}
      rodape={
        <>
          <Botao type="submit" form="form-assinatura" onClick={() => (enviarDepois.current = false)}>
            {t('acoes.salvarRascunho')}
          </Botao>
          {sessao.pode('documentos.enviar') && (
            <Botao variante="primario" type="submit" form="form-assinatura" icone={<Send className="size-4" />} aria-disabled={semPdf || undefined} onClick={() => (enviarDepois.current = true)}>
              {t('acoes.salvarEnviar')}
            </Botao>
          )}
        </>
      }
    >
      <div className="mb-3 flex flex-col gap-2">
        <div className="min-w-0 sm:max-w-sm">
          <Rotulo id="assinatura-provedor" rotulo={t('campos.provedor')} ajuda={t('campos.provedorAjuda')}>
            <Selecao id="assinatura-provedor" value={provedor} onChange={(e) => trocarProvedor(e.target.value as ProvedorAssinatura)} opcoes={PROVEDORES.map((p) => ({ valor: p, rotulo: rotuloProvedor(t, p, padrao.data?.conectados) }))} />
          </Rotulo>
        </div>
        <p className="text-sm text-tinta-3">{externo ? t('ajuda.assinaturaProvedor', { provedor: t(`provedor.${provedor}`) }) : t('ajuda.assinatura')}</p>
        {doContrato &&
          (sugestao.isPending ? (
            <Carregando />
          ) : sugestao.isError ? (
            <p className="text-sm text-tinta-2" role={semPdf ? 'alert' : undefined}>
              {mensagemDeErro(sugestao.error)}
            </p>
          ) : (
            <>
              <p className="text-sm text-tinta-3">
                {t('envioContrato.sugerido')} {sugestao.data.provedor_origem === 'documento' ? t('envioContrato.provedorDocumento', { provedor: t(`provedor.${sugestao.data.provedor}`) }) : ''}
              </p>
              {sugestao.data.avisos.map((a) => (
                <p key={a} className="text-sm text-tinta-2">
                  {t(`envioContrato.avisos.${a}`)}
                </p>
              ))}
            </>
          ))}
      </div>
      <Formulario
        id="form-assinatura"
        campos={campos}
        inicial={{}}
        aoEnviar={async (v) => {
          if (semPdf && enviarDepois.current) {
            toast.erro(mensagemDeErro(sugestao.error));
            return;
          }
          if (!lista.length) {
            toast.erro(t('vazio.signatarios'));
            return;
          }
          const signatarios = lista.map((x) =>
            x.tipo === 'interno'
              ? { tipo: 'interno', usuario_id: x.usuario_id }
              : x.tipo === 'cliente'
                ? { tipo: 'cliente', parceiro_id: x.parceiro_id, acesso_id: x.acesso_id }
                : { tipo: 'externo', nome: x.nome, email: x.email, parceiro_id: x.parceiro_id ?? null },
          );
          await post(
            '/documentos/assinaturas',
            { id, documento_id: documento?.id ?? v.documento_id, signatarios, mensagem: String(v.mensagem ?? '').trim() || null, prazo: v.prazo || null, provedor },
            { 'Idempotency-Key': id },
          );
          if (enviarDepois.current) await post(`/documentos/assinaturas/${id}/enviar`);
          await invalidarDocumentos(qc);
          toast.sucesso(enviarDepois.current ? t('enviadoSucesso') : tn('comum.salvo'));
          aoFechar();
        }}
      >
        <div className="flex flex-col gap-3 sm:col-span-6">
          <span className="text-sm font-medium text-tinta-2">{t('campos.signatarios')}</span>
          {lista.length ? (
            <ul className="flex flex-col divide-y divide-borda rounded-lg border border-borda">
              {lista.map((x) => (
                <li key={x.chave} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
                  <span className="min-w-0 grow basis-24">
                    <span className="block font-medium text-tinta-1">{nomePessoa(x.nome)}</span>
                    {x.papel && <span className="block text-xs text-tinta-3">{t(`envioContrato.papel.${x.papel}`)}</span>}
                    {x.email && <span className="block text-tinta-3">{email(x.email)}</span>}
                  </span>
                  <span className="min-w-min">
                    <Selo>{t(`signatario.${externo ? 'externo' : x.tipo}`)}</Selo>
                  </span>
                  <Botao variante="fantasma" tamanho="sm" icone={<X className="size-4" />} aria-label={t('acoes.remover', { nome: nomePessoa(x.nome) })} onClick={() => setLista((l) => l.filter((y) => y.chave !== x.chave))} />
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-tinta-3">{t('vazio.signatarios')}</p>
          )}
          <Rotulo id="signatario-equipe" rotulo={t('acoes.adicionarEquipe')}>
            <BuscaRemota
              id="signatario-equipe"
              def={{ caminho: '/membros', rotulo: (i) => String(i.nome ?? ''), pessoa: true }}
              valor={null}
              aoMudar={(v, item) => v && incluir({ tipo: 'interno', usuario_id: v, nome: String(item?.nome ?? '') })}
            />
          </Rotulo>
          <Rotulo id="signatario-cliente" rotulo={t('acoes.adicionarCliente')}>
            <BuscaRemota id="signatario-cliente" def={{ caminho: '/cadastros/parceiros', params: { operacao: true }, rotulo: (i) => String(i.nome ?? '') }} valor={parceiro} aoMudar={(v) => (setParceiro(v), setAcesso(''))} />
          </Rotulo>
          {externo && (
            <div className="flex min-w-0 flex-wrap items-end gap-2" data-signatario-email>
              <div className="min-w-0 grow basis-40">
                <Rotulo id="signatario-nome" rotulo={t('campos.nomeCompleto')}>
                  <EntradaSensivel id="signatario-nome" mascara="nome" maxLength={200} value={novoNome} onChange={(e) => setNovoNome(e.target.value)} />
                </Rotulo>
              </div>
              <div className="min-w-0 grow basis-48">
                <Rotulo id="signatario-email" rotulo={t('campos.email')}>
                  <EntradaSensivel id="signatario-email" mascara="email" type="email" maxLength={254} value={novoEmail} onChange={(e) => setNovoEmail(e.target.value)} />
                </Rotulo>
              </div>
              <Botao icone={<Mail className="size-4" />} aria-disabled={!novoNome.trim() || !novoEmail.trim()} onClick={incluirEmail}>
                {t('acoes.adicionarEmail')}
              </Botao>
            </div>
          )}
          {parceiro &&
            (acessos.isPending ? (
              <Carregando />
            ) : acessos.isError ? (
              <Erro erro={acessos.error} aoTentar={() => acessos.refetch()} />
            ) : !acessos.data.portal ? (
              externo ? null : <p className="text-sm text-tinta-3">{t('vazio.semPortal')}</p>
            ) : !acessos.data.itens.length ? (
              externo ? null : <p className="text-sm text-tinta-3">{t('vazio.acessos')}</p>
            ) : (
              <div className="flex min-w-0 flex-wrap items-end gap-2">
                <div className="min-w-0 grow basis-48">
                  <Selecao
                    aria-label={t('campos.acessoCliente')}
                    value={acesso}
                    onChange={(e) => setAcesso(e.target.value)}
                    vazio={t('campos.acessoCliente')}
                    opcoes={acessos.data.itens.map((a) => ({ valor: a.acesso_id, rotulo: a.email ? `${nomePessoa(a.nome)} (${email(a.email)})` : nomePessoa(a.nome) }))}
                  />
                </div>
                <Botao
                  icone={<UserPlus className="size-4" />}
                  aria-disabled={!acesso}
                  onClick={() => {
                    const a = acessos.data.itens.find((x) => x.acesso_id === acesso);
                    if (a) incluir({ tipo: 'cliente', parceiro_id: a.parceiro_id, acesso_id: a.acesso_id, nome: a.nome });
                  }}
                >
                  {t('acoes.adicionarCliente')}
                </Botao>
              </div>
            ))}
        </div>
      </Formulario>
    </Drawer>
  );
}


type Acao = 'assinar' | 'recusar' | 'cancelar' | 'excluir' | null;

/** Ficha da assinatura: documento, quem assina, a trilha imutável e as ações (assinar, recusar, enviar, cancelar). */
function FichaAssinatura({ id, aoFechar }: { id: string; aoFechar: () => void }) {
  const { t } = useTranslation('documentos');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const [acao, setAcao] = useState<Acao>(null);
  const [motivo, setMotivo] = useState('');
  const q = useQuery({ queryKey: ['documentos', 'assinatura', id], queryFn: () => get<AssinaturaDetalhe>(`/documentos/assinaturas/${id}`) });
  const mutar = useMutation({
    mutationFn: ({ caminho, corpo, metodo }: { caminho: string; corpo?: unknown; metodo?: 'DELETE' }) => (metodo === 'DELETE' ? del(caminho) : post(caminho, corpo)),
    onSuccess: async (_r, v) => {
      await invalidarDocumentos(qc);
      toast.sucesso(v.caminho.endsWith('/assinar') ? t('assinadoSucesso') : v.caminho.endsWith('/enviar') ? t('enviadoSucesso') : tn('comum.salvo'));
      setAcao(null);
      setMotivo('');
      if (v.metodo === 'DELETE') aoFechar();
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const a = q.data;
  const base = `/documentos/assinaturas/${id}`;
  // Val5 (29/10): enviar e cancelar a assinatura têm ações próprias (`documentos.enviar`, `documentos.cancelar`).
  const podeCancelar = a && s.pode('documentos.cancelar') && (a.estado === 'rascunho' || a.estado === 'enviado');
  const podeAtualizar = a && a.pode_editar && a.provedor !== 'nativa' && a.no_provedor && (a.estado === 'enviado' || (a.estado === 'assinado' && !a.anexo_assinado_id));
  return (
    <>
      <Drawer
        aberto={acao === null || acao === 'assinar' || acao === 'excluir'}
        aoFechar={aoFechar}
        largura="lg"
        titulo={a ? textoLivre(a.documento_nome) : t('campos.assinatura')}
        rodape={
          a ? (
            <>
              {a.estado === 'rascunho' && s.pode('documentos.excluir') && (
                <Botao variante="perigo" icone={<Trash2 className="size-4" />} onClick={() => setAcao('excluir')}>
                  {t('acoes.excluir')}
                </Botao>
              )}
              {podeCancelar && (
                <Botao icone={<Ban className="size-4" />} onClick={() => setAcao('cancelar')}>
                  {t('acoes.cancelar')}
                </Botao>
              )}
              {a.minha_pendente && (
                <Botao icone={<X className="size-4" />} onClick={() => setAcao('recusar')}>
                  {t('acoes.recusar')}
                </Botao>
              )}
              {podeAtualizar && (
                <Botao icone={<RefreshCw className="size-4" />} carregando={mutar.isPending} onClick={() => mutar.mutate({ caminho: `${base}/atualizar` })}>
                  {t('acoes.atualizar')}
                </Botao>
              )}
              {a.anexo_assinado_id && (
                <a className="inline-flex items-center gap-1 text-sm text-acento-forte hover:underline" download href={`/api/v1/anexos/${a.anexo_assinado_id}`}>
                  <Download className="size-4" aria-hidden />
                  {t('acoes.baixarAssinado')}
                </a>
              )}
              {a.estado !== 'rascunho' && (
                <a className="inline-flex items-center gap-1 text-sm text-acento-forte hover:underline" download href={`/api/v1${base}/comprovante`}>
                  <FileCheck2 className="size-4" aria-hidden />
                  {t('acoes.comprovante')}
                </a>
              )}
              {a.minha_pendente ? (
                <Botao variante="primario" icone={<PenLine className="size-4" />} onClick={() => setAcao('assinar')}>
                  {t('acoes.assinar')}
                </Botao>
              ) : a.estado === 'rascunho' && a.pode_editar && s.pode('documentos.enviar') ? (
                <Botao variante="primario" icone={<Send className="size-4" />} carregando={mutar.isPending} onClick={() => mutar.mutate({ caminho: `${base}/enviar` })}>
                  {t('acoes.enviar')}
                </Botao>
              ) : null}
            </>
          ) : (
            <Botao variante="fantasma" onClick={aoFechar}>
              {tn('comum.fechar')}
            </Botao>
          )
        }
      >
        {q.isPending ? (
          <Carregando />
        ) : q.isError ? (
          <Erro erro={q.error} aoTentar={() => q.refetch()} />
        ) : a ? (
          <div className="flex flex-col gap-5">
            <dl>
              <Linha rotulo={t('campos.estado')}>
                <Selo cor={COR_ASSINATURA[a.estado]}>{t(`estado.${a.estado}`)}</Selo>
              </Linha>
              <Linha rotulo={t('campos.provedor')}>
                <span className="block">{t(`provedor.${a.provedor}`)}</span>
                {a.provedor !== 'nativa' && a.estado === 'enviado' && !a.no_provedor && !a.provedor_erro && <span className="block text-tinta-3">{t('ajuda.enviando', { provedor: t(`provedor.${a.provedor}`) })}</span>}
                {a.provedor_erro && <span className="block text-perigo">{t('ajuda.provedorErro', { erro: textoLivre(a.provedor_erro) })}</span>}
              </Linha>
              <Linha rotulo={t('campos.documento')}>
                <Link className="text-acento-forte hover:underline" to={`/cadastros/documentos/arquivos?id=${a.documento_id}`}>
                  {t('acoes.abrirDocumento')}
                </Link>
              </Linha>
              <Linha rotulo={t('campos.versao')}>
                {a.anexo_id ? (
                  <a className="inline-flex items-center gap-1 text-acento-forte hover:underline" download href={`/api/v1/anexos/${a.anexo_id}`}>
                    <Download className="size-4" aria-hidden />
                    {t('acoes.baixarVersao', { versao: a.versao })}
                  </a>
                ) : (
                  t('versaoN', { versao: a.versao })
                )}
              </Linha>
              <Linha rotulo={t('campos.hash')}>
                <HashCurto hash={a.sha256_documento} />
              </Linha>
              {a.mensagem && <Linha rotulo={t('campos.mensagem')}>{textoLivre(a.mensagem)}</Linha>}
              <Linha rotulo={t('campos.prazo')}>{a.prazo ? data(a.prazo) : '-'}</Linha>
              <Linha rotulo={t('campos.enviada')}>{a.enviada_em ? dataHora(a.enviada_em) : '-'}</Linha>
              {a.concluida_em && <Linha rotulo={t('campos.concluida')}>{dataHora(a.concluida_em)}</Linha>}
              {a.motivo && <Linha rotulo={t('campos.motivo')}>{textoLivre(a.motivo)}</Linha>}
            </dl>
            <section>
              <h3 className="mb-2 text-sm font-semibold text-tinta-1">{t('campos.signatarios')}</h3>
              <ul className="flex flex-col divide-y divide-borda">
                {a.signatarios.map((x) => (
                  <li key={x.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
                    <span className="min-w-0 grow basis-24">
                      <span className="block font-medium text-tinta-1">{nomePessoa(x.nome)}</span>
                      <span className="block text-tinta-3">
                        {t(`signatario.${x.tipo}`)}
                        {x.decidido_em ? ` · ${dataHora(x.decidido_em)}` : ''}
                        {x.ip ? ` · ${t('campos.ip')} ${x.ip}` : ''}
                      </span>
                    </span>
                    <span className="min-w-min">
                      <Selo cor={COR_SIGNATARIO[x.estado]}>{t(`signatario.${x.estado}`)}</Selo>
                    </span>
                  </li>
                ))}
              </ul>
            </section>
            <section>
              <h3 className="mb-1 text-sm font-semibold text-tinta-1">{t('campos.trilha')}</h3>
              <p className="mb-2 text-xs text-tinta-3">{t('ajuda.trilha')}</p>
              <ol className="flex flex-col gap-2">
                {a.trilha.map((l) => (
                  <li key={l.ordem} className="rounded-lg border border-borda px-3 py-2 text-sm">
                    <span className="block font-medium text-tinta-1">
                      {l.ordem}. {t(`evento.${l.evento}`)}
                    </span>
                    <span className="block text-tinta-3">
                      {dataHora(l.created_at)} · {t(`ator.${l.ator_tipo}`)}
                      {l.ator_nome ? `: ${nomePessoa(l.ator_nome)}` : ''}
                      {l.ip ? ` · ${t('campos.ip')} ${l.ip}` : ''}
                    </span>
                    <span className="block text-tinta-3">
                      {t('campos.hash')} <HashCurto hash={l.hash} />
                    </span>
                  </li>
                ))}
              </ol>
            </section>
          </div>
        ) : null}
      </Drawer>
      <Confirmar
        aberto={acao === 'assinar'}
        aoFechar={() => setAcao(null)}
        aoConfirmar={() => mutar.mutate({ caminho: `${base}/assinar` })}
        titulo={t('confirmar.assinar')}
        texto={t('ajuda.assinar')}
        carregando={mutar.isPending}
      />
      <Confirmar
        aberto={acao === 'excluir'}
        aoFechar={() => setAcao(null)}
        aoConfirmar={() => mutar.mutate({ caminho: base, metodo: 'DELETE' })}
        titulo={t('confirmar.excluirAssinatura')}
        texto={t('confirmar.excluirAssinaturaTexto')}
        perigoso
        carregando={mutar.isPending}
      />
      <Modal
        aberto={acao === 'recusar' || acao === 'cancelar'}
        aoFechar={() => setAcao(null)}
        titulo={acao === 'cancelar' ? t('cancelar.titulo') : t('recusar.titulo')}
        rodape={
          <>
            <Botao variante="fantasma" onClick={() => setAcao(null)}>
              {tn('comum.cancelar')}
            </Botao>
            <Botao variante="primario" carregando={mutar.isPending} aria-disabled={!motivo.trim()} onClick={() => motivo.trim() && mutar.mutate({ caminho: `${base}/${acao === 'cancelar' ? 'cancelar' : 'recusar'}`, corpo: { motivo } })}>
              {acao === 'cancelar' ? t('acoes.cancelar') : t('acoes.recusar')}
            </Botao>
          </>
        }
      >
        <Rotulo id="assinatura-motivo" rotulo={t('campos.motivo')} obrigatorio ajuda={acao === 'cancelar' ? t('cancelar.ajuda') : t('recusar.ajuda')}>
          <AreaSensivel
            id="assinatura-motivo"
            rows={3}
            maxLength={500}
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            className="w-full min-w-0 rounded-lg border border-borda-campo bg-campo px-3 py-2 text-sm text-tinta-1 focus:border-foco focus:outline-2 focus:outline-offset-0 focus:outline-foco"
          />
        </Rotulo>
      </Modal>
    </>
  );
}
